/**
 * datetime.js — the ONE place where the user's preferred timezone
 * (Settings › Preferences) is applied to date rendering.
 *
 * Two mechanisms make every date in the UI honor the preference:
 *
 *  1. ``installTimezoneShim()`` (called once from index.js) wraps
 *     ``Date.prototype.toLocaleDateString / toLocaleTimeString /
 *     toLocaleString`` so any call that doesn't explicitly pass a
 *     ``timeZone`` option renders in the user's timezone. That covers
 *     the majority of the app without touching call sites.
 *
 *  2. A drop-in replacement for date-fns' ``format`` that shifts the
 *     instant into the user's timezone before formatting. Files that
 *     used ``import { format } from 'date-fns'`` import from here
 *     instead — same signature, same tokens. The other date-fns
 *     helpers are re-exported unchanged so a single import line swap
 *     is enough (duration/relative helpers like ``formatDistanceToNow``
 *     compare instants and are timezone-independent by nature).
 *
 * The preference itself lives on the user doc (``users.timezone``,
 * edited in Settings › Preferences) and is mirrored into
 * ``localStorage.user`` on login and on profile save.
 */

import { format as dateFnsFormat } from 'date-fns';
import moment from 'moment-timezone';

// Re-exports so callers can swap 'date-fns' for this module wholesale.
export {
  formatDistanceToNow,
  formatDistance,
  addDays,
  subDays,
  addHours,
  addMonths,
  addSeconds,
  differenceInSeconds,
  parseISO,
  parse,
  startOfDay,
  isValid,
} from 'date-fns';

/** The user's preferred IANA timezone: profile setting first, browser
 *  timezone as the fallback for users who never set one. */
export const getUserTimezone = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('user') || 'null');
    const tz = stored?.timezone;
    if (tz && isValidTimezone(tz)) return tz;
  } catch (_e) { /* corrupted localStorage — fall through */ }
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (_e) {
    return 'UTC';
  }
};

const tzValidityCache = {};
const isValidTimezone = (tz) => {
  if (tz in tzValidityCache) return tzValidityCache[tz];
  try {
    // Throws RangeError for unknown zone names.
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    tzValidityCache[tz] = true;
  } catch (_e) {
    tzValidityCache[tz] = false;
  }
  return tzValidityCache[tz];
};

/**
 * Return a Date whose *local-clock* fields equal the wall time of the
 * given instant in the user's timezone. This is the standard trick for
 * timezone-aware formatting with libraries (like date-fns) that only
 * read a Date's local fields — the returned Date is for FORMATTING
 * ONLY and must not be used as an instant.
 */
export const toUserTz = (value) => {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return d;
  const tz = getUserTimezone();
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    }).formatToParts(d);
    const get = (type) => Number(parts.find((p) => p.type === type)?.value);
    return new Date(
      get('year'),
      get('month') - 1,
      get('day'),
      // 'h23' quirk: midnight can come back as "24".
      get('hour') % 24,
      get('minute'),
      get('second'),
      d.getMilliseconds(),
    );
  } catch (_e) {
    return d;
  }
};

/** date-fns ``format``, rendered in the user's preferred timezone.
 *  Same signature and format tokens as the original. */
export const format = (date, fmt, options) =>
  dateFnsFormat(toUserTz(date), fmt, options);

/**
 * Patch the three ``Date.prototype.toLocale*`` methods so every call
 * without an explicit ``timeZone`` renders in the user's preferred
 * timezone. Explicit timeZone options are respected untouched.
 * Idempotent; call once at app bootstrap.
 */
export const installTimezoneShim = () => {
  // Moment-based views (legacy datasource containers) follow the same
  // preference: setDefault makes every moment() render in the user tz.
  try {
    moment.tz.setDefault(getUserTimezone());
  } catch (_e) { /* invalid zone — moment keeps browser local time */ }

  if (Date.prototype.toLocaleString.__tzShimmed) return;
  ['toLocaleDateString', 'toLocaleTimeString', 'toLocaleString'].forEach((method) => {
    const original = Date.prototype[method];
    const shimmed = function shimmedToLocale(locales, options) {
      if (options && options.timeZone) {
        return original.call(this, locales, options);
      }
      try {
        return original.call(this, locales, {
          ...(options || {}),
          timeZone: getUserTimezone(),
        });
      } catch (_e) {
        // Bad option combination or invalid zone — never break rendering.
        return original.call(this, locales, options);
      }
    };
    shimmed.__tzShimmed = true;
    // eslint-disable-next-line no-extend-native
    Date.prototype[method] = shimmed;
  });
};
